import { useParams, useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import StyledButton from "../../components/Button.tsx";
import { getAdmin } from "../../services/admins.service.ts";
import { Admin } from "../../types/Admin.ts";
import { NarrowPageContainer, PageTitle } from "../../components/Layout.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";

const FormCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  border-left: 4px solid #64da4c;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const LoadingText = styled.div`
  color: #001f52;
  padding: 2rem;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  transition: background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    color: #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  border-left: 4px solid #c62828;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    background-color: #3a1f1f;
    color: #ff6b6b;
    border-left-color: #ff6b6b;
  }
`;

export default function AdminDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("Admin ID mangler");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getAdmin(id)
      .then((admin) => {
        setAdmin(admin);
      })
      .catch((err) => {
        setError(parseApiError(err));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return <NarrowPageContainer><LoadingText>Indlæser admin...</LoadingText></NarrowPageContainer>;
  }

  if (error || !admin) {
    return (
      <NarrowPageContainer>
        <FormCard>
          <ErrorMessage>{error || "Admin ikke fundet"}</ErrorMessage>
          <StyledButton onClick={() => navigate("/admins")}>← Tilbage til admins</StyledButton>
        </FormCard>
      </NarrowPageContainer>
    );
  }

  return (
    <NarrowPageContainer>
      <FormCard>
        <PageTitle as="h2">Admin detaljer</PageTitle>
        <div style={{ marginBottom: "1rem" }}>
          <strong>Navn:</strong> {admin.name}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <strong>Email:</strong> {admin.email}
        </div>
        {admin.createdAt && (
          <div style={{ 
            marginBottom: "1rem", 
            color: "#666", 
            fontSize: "0.9rem",
            transition: "color 0.3s ease"
          }} className="dark-mode-text-secondary">
            <strong>Oprettet:</strong> {new Date(admin.createdAt).toLocaleString("da-DK")}
          </div>
        )}
        <StyledButton onClick={() => navigate("/admins")}>← Tilbage til admins</StyledButton>
      </FormCard>
    </NarrowPageContainer>
  );
}
